import React, { useEffect, useMemo, useState } from "react";
import {
  getAllMessagesContactForm,
  markMessageContactForm,
  deleteMessageContactForm,
} from "../../services/contactformServices";
import { useUser } from "../../contexts/UserContext";
import ButtonDelete from "../../components/Button/ButtonDelete";
import ButtonEnvelopeToggle from "../../components/Button/ButtonEnvelopeToggle";

const DEMO_ROLES = [2]; // ajuste si besoin

export default function MessagesSection() {
  const { user } = useUser();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const isDemoMode = DEMO_ROLES.includes(user?.role_id);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await getAllMessagesContactForm();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const sortedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      ),
    [messages]
  );

  const toggleReadStatus = async (messageId) => {
    if (isDemoMode) {
      return;
    }
    const msg = messages.find((m) => m.id === messageId);
    if (!msg) return;

    try {
      await markMessageContactForm(messageId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, read_message: !m.read_message } : m
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (isDemoMode) {
      return;
    }
    const ok = window.confirm("Supprimer ce message ?");
    if (!ok) return;

    try {
      await deleteMessageContactForm(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (e) {
      console.error(e);
    }
  };

  const unreadCount = messages.filter((m) => !m.read_message).length;

  return (
    <div className="mt-8 font-montserrat">
      <div className="flex items-center justify-between mb-3">
        <h3 className="card-title text-gray-900 dark:text-white">
          Messages du Formulaire de Contact
        </h3>
        <div className="text-sm opacity-80">
          <span className="mr-3">
            Total: <b>{messages.length}</b>
          </span>
          <span>
            Non lus:{" "}
            <b className={unreadCount > 0 ? "text-red-500" : ""}>
              {unreadCount}
            </b>
          </span>
        </div>
      </div>

      {loading ? (
        <div className="p-4 rounded bg-gray-100 dark:bg-gray-800">
          Chargement...
        </div>
      ) : messages.length === 0 ? (
        <p className="text-center text-gray-900 dark:text-gray-100">
          Pas encore de messages.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-50 dark:bg-gray-800 divide-y divide-gray-300 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr className="bg-slate-400 dark:bg-gray-900">
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-white">
                  Date
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-white">
                  Message
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-white">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedMessages.map((message) => (
                <tr
                  key={message.id}
                  className={`hover:bg-gray-200 dark:hover:bg-gray-700 ${
                    message.read_message
                      ? "bg-gray-100 dark:bg-gray-800"
                      : "bg-white dark:bg-gray-800/70"
                  }`}
                >
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {new Date(message.created_at).toLocaleString("fr-FR", {
                      year: "2-digit",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-xl">
                    <p className="font-bold">{message.name}</p>
                    <p className="whitespace-pre-wrap">{message.message}</p>
                    <p className="text-xs opacity-70 mt-1">{message.email}</p>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-center items-center gap-2">
                      <ButtonEnvelopeToggle
                        isRead={message.read_message}
                        onClick={() => toggleReadStatus(message.id)}
                      />
                      <ButtonDelete
                        onClick={() => handleDeleteMessage(message.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Recharge rapide */}
          <div className="flex justify-end mt-3">
            <button
              onClick={loadMessages}
              className="btn btn-sm btn-outline"
              type="button"
            >
              Recharger
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
